import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-light-svg-icons';

import card from './RoomOptionCards.module.scss';
import { ConfiguratorGroupDto } from '@/services/configurator';
import { useOptionConfigurator } from '../../../OptionConfiguratorContext';
import DateDisplay from '@/components/localization/DateDisplay';
import { RoomStyleCardSection } from '../../RoomStyle/RoomStyleCardSection';
import { StyleSelectionGate } from '../../RoomStyle/StyleSelectionGate';
import { GroupCard } from './GroupCard';
import { GroupOptionDetail } from './GroupOptionDetail';
import { GroupProductPreview } from './GroupProductPreview';
import { groupSelectedOption } from './helpers';
import { useActiveGroup } from './useActiveGroup';

interface RoomOptionCardsProps {
    groups: ConfiguratorGroupDto[];
    /** Fired when a group is opened — used to pre-load that group's configurator visuals. */
    onGroupExpand?: (group: ConfiguratorGroupDto) => void;
}

/**
 * Centered card-grid layout for the immersive configurator when a room has no visualization. The
 * option groups (and the room's styles) are shown as two-column cards in a responsive grid, capped
 * to a comfortable reading width — replacing the cramped master/detail split's side *filter* panel.
 *
 * Opening a group card keeps the card grid in the main column (covered by the selected-product
 * preview overlay) and reveals that group's option selection in the side panel — the same
 * master/detail interaction the visualized configurator uses.
 */
export const RoomOptionCards: React.FC<RoomOptionCardsProps> = ({ groups, onGroupExpand }) => {
    const { projectData, rooms } = useOptionConfigurator();
    const { roomId } = useParams<{ roomId: string }>();
    const { activeGroup, openGroup, closeGroup, swallowGhostClick } = useActiveGroup(groups, onGroupExpand);

    // The selected product previewed over the card grid for the open group — live from context, so it
    // updates as soon as a new option is picked in the side panel. No default fallback: when nothing
    // is selected the overlay shows a prompt message instead (handled in GroupProductPreview).
    const selectedOption = useMemo(() => {
        if (!activeGroup) return null;
        const room = rooms.find((r) => r.roomId === Number(roomId));
        const live = room?.groups.find((g) => g.id === activeGroup.id) ?? activeGroup;
        return groupSelectedOption(live);
    }, [rooms, roomId, activeGroup]);

    const deadlineById = new Map((projectData?.deadlines ?? []).map((d) => [d.id, d]));

    // Organise the menu by deadline: one section per deadline present in this room, each headed by a
    // separator showing the deadline name (left) and its date (right), ordered chronologically.
    const byDeadline = new Map<number, ConfiguratorGroupDto[]>();
    for (const group of groups) {
        const list = byDeadline.get(group.deadlineId);
        if (list) list.push(group);
        else byDeadline.set(group.deadlineId, [group]);
    }
    const orderedIds = [...byDeadline.keys()].sort((a, b) => {
        const da = deadlineById.get(a)?.deadline;
        const db = deadlineById.get(b)?.deadline;
        return new Date(da ?? 0).getTime() - new Date(db ?? 0).getTime();
    });

    const grid = (
        // The grid stays mounted behind the open detail panel, so the ghost click that follows
        // closing it would otherwise re-open a group card — swallow taps for a moment after close.
        <div className={card.page} onClickCapture={swallowGhostClick}>
            <div className={card.container}>
                <RoomStyleCardSection />
                {orderedIds.map((id) => {
                    const meta = deadlineById.get(id);
                    return (
                        <section key={id} className={card.section}>
                            <div className={card.sectionHeader}>
                                <span className={card.sectionLabel}>
                                    <FontAwesomeIcon className={card.sectionIcon} icon={faCalendar} />
                                    <span className={card.sectionName}>
                                        {meta?.name ?? <Trans>Other options</Trans>}
                                    </span>
                                </span>
                                {meta?.deadline && (
                                    <span className={card.sectionDate}>
                                        <DateDisplay value={meta.deadline} monthTextForm />
                                    </span>
                                )}
                            </div>
                            <div className={card.grid}>
                                {byDeadline.get(id)?.map((group) => (
                                    <GroupCard
                                        key={`${group.deadlineId}-${group.id}`}
                                        group={group}
                                        onClick={() => openGroup(group)}
                                    />
                                ))}
                            </div>
                        </section>
                    );
                })}
            </div>
        </div>
    );

    // No group open: the centered card grid fills the whole area (no side panel). A group open: the
    // card grid keeps its full width in the background (so it doesn't shift or reflow), with the
    // product-preview overlay and the option-selection side panel laid over it.
    const content = !activeGroup ? (
        grid
    ) : (
        <div className={card.split}>
            <div className={card.master}>{grid}</div>
            <div className={card.previewHost}>
                <GroupProductPreview option={selectedOption} groupName={activeGroup.name} />
            </div>
            <div className={card.splitDetail}>
                <GroupOptionDetail group={activeGroup} is3d={false} onClose={closeGroup} />
            </div>
        </div>
    );

    // Gate the whole menu behind style selection: a room with unpicked required styles shows a
    // "choose your style" prompt instead of the option groups until the user resolves it.
    return <StyleSelectionGate>{content}</StyleSelectionGate>;
};
